import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { hash } from "@ember/helper";
import { action } from "@ember/object";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import willDestroy from "@ember/render-modifiers/modifiers/will-destroy";
import { LinkTo } from "@ember/routing";
import { cancel } from "@ember/runloop";
import { service } from "@ember/service";
import { trustHTML } from "@ember/template";
import { modifier as modifierFn } from "ember-modifier";
import { popupAjaxError } from "discourse/lib/ajax-error";
import { bind } from "discourse/lib/decorators";
import discourseLater from "discourse/lib/later";
import { and, eq } from "discourse/truth-helpers";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import dReplaceEmoji from "discourse/ui-kit/helpers/d-replace-emoji";
import { i18n } from "discourse-i18n";
import ChannelIcon from "discourse/plugins/chat/discourse/components/channel-icon";
import ChannelName from "discourse/plugins/chat/discourse/components/channel-name";
import ChatChannelMetadata from "discourse/plugins/chat/discourse/components/chat-channel-metadata";
import ChatChannelSidebarContextMenu from "discourse/plugins/chat/discourse/components/chat-channel-sidebar-context-menu";
import ToggleChannelMembershipButton from "discourse/plugins/chat/discourse/components/toggle-channel-membership-button";
import ChatOnLongPress from "discourse/plugins/chat/discourse/modifiers/chat/on-long-press";

const FADEOUT_CLASS = "-fade-out";

export default class ChatChannelRow extends Component {
  @service capabilities;
  @service chat;
  @service chatApi;
  @service menu;
  @service site;

  @tracked isAtThreshold = false;
  @tracked shouldRemoveChannel = false;
  @tracked showRemoveButton = false;
  @tracked swipableRow = null;
  @tracked shouldReset = false;
  @tracked diff = 0;
  @tracked rowStyle = null;
  @tracked isLongPressing = false;

  registerSwipableRow = modifierFn((element) => {
    this.swipableRow = element;
  });

  onReset = modifierFn((element) => {
    const handler = () => {
      this.rowStyle = trustHTML("margin-right: 0px;");
      this.showRemoveButton = false;
      this.shouldReset = false;
    };

    element.addEventListener("transitionend", handler, { once: true });

    return () => {
      element.removeEventListener("transitionend", handler);
      this.rowStyle = trustHTML("margin-right: 0px;");
      this.showRemoveButton = false;
      this.shouldReset = false;
    };
  });

  onRemoveChannel = modifierFn((element) => {
    element.addEventListener(
      "transitionend",
      () => {
        this.chat.unfollowChannel(this.args.channel).catch(popupAjaxError);
      },
      { once: true }
    );

    element.classList.add(FADEOUT_CLASS);
  });

  handleSwipe = modifierFn((element) => {
    element.addEventListener("touchstart", this.onSwipeStart, {
      passive: true,
    });
    element.addEventListener("touchmove", this.onSwipe, {
      passive: true,
    });
    element.addEventListener("touchend", this.onSwipeEnd);

    return () => {
      element.removeEventListener("touchstart", this.onSwipeStart);
      element.removeEventListener("touchmove", this.onSwipe);
      element.removeEventListener("touchend", this.onSwipeEnd);
    };
  });

  willDestroy() {
    super.willDestroy(...arguments);
    cancel(this._longPressActivationHandler);
    cancel(this._removeClickSuppressorHandler);
  }

  @bind
  onSwipeStart(event) {
    this._initialX = event.changedTouches[0].screenX;
  }

  @bind
  onSwipe(event) {
    this.showRemoveButton = true;
    this.shouldReset = false;
    this.isAtThreshold = false;

    const threshold = window.innerWidth / 3;
    const touchX = event.changedTouches[0].screenX;

    this.diff = this._initialX - touchX;
    this.isAtThreshold = this.diff >= threshold;

    // ensures we will go back to the initial position when swiping very fast
    if (this.diff > 25) {
      if (this.isAtThreshold) {
        this.diff = threshold + (this.diff - threshold) * 0.1;
      }

      this.rowStyle = trustHTML(`margin-right: ${this.diff}px;`);
    } else {
      this.rowStyle = trustHTML("margin-right: 0px;");
    }
  }

  @bind
  onSwipeEnd() {
    if (this.isAtThreshold) {
      if (this.isSwipeToClearNotifications) {
        this.#clearNotifications();
        this.shouldReset = true;
      } else {
        this.rowStyle = trustHTML("margin-right: 0px;");
        this.shouldRemoveChannel = true;
      }
    } else {
      this.shouldReset = true;
    }
  }

  #clearNotifications() {
    const channel = this.args.channel;
    if (!channel.currentUserMembership) {
      return;
    }

    const lastMessageId = channel.lastMessage?.id;
    channel.tracking.reset();
    channel.updateLastViewedAt();

    if (lastMessageId) {
      this.chatApi
        .markChannelAsRead(channel.id, lastMessageId)
        .catch(popupAjaxError);
    }
  }

  @bind
  suppressEvent(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  @action
  onLongPressStart(element) {
    // suppress the native context menu during the press
    element.addEventListener("contextmenu", this.suppressEvent);

    this._longPressActivationHandler = discourseLater(() => {
      this.isLongPressing = true;
    }, 125);
  }

  @action
  onLongPressCancel(element) {
    cancel(this._longPressActivationHandler);
    this.isLongPressing = false;

    element.removeEventListener("contextmenu", this.suppressEvent);

    this._removeClickSuppressorHandler = discourseLater(() => {
      element.removeEventListener("click", this.suppressEvent, {
        capture: true,
      });
    }, 200);
  }

  @action
  onLongPressEnd(element) {
    cancel(this._longPressActivationHandler);
    this.isLongPressing = false;

    // stop the release from clicking through to the channel link
    element.addEventListener("click", this.suppressEvent, { capture: true });

    document.activeElement?.blur();

    this.menu.show(element, {
      identifier: this.args.channel.isDirectMessageChannel
        ? "chat-direct-message-channel-menu"
        : "chat-channel-menu",
      component: ChatChannelSidebarContextMenu,
      modalForMobile: true,
      data: { channel: this.args.channel },
    });
  }

  get shouldHandleSwipe() {
    if (!this.capabilities.touch) {
      return false;
    }

    return this.args.channel.isDirectMessageChannel || this.channelHasUnread;
  }

  get isSwipeToClearNotifications() {
    return !this.args.channel.isDirectMessageChannel;
  }

  get leaveDirectMessageLabel() {
    return i18n("chat.direct_messages.close");
  }

  get leaveChannelLabel() {
    return i18n("chat.channel_settings.leave_channel");
  }

  get channelHasUnread() {
    return (
      this.args.channel.tracking.unreadCount > 0 ||
      this.args.channel.unreadThreadsCountSinceLastViewed > 0
    );
  }

  get shouldRenderLastMessage() {
    return (
      this.site.mobileView &&
      this.args.channel.isDirectMessageChannel &&
      this.args.channel.lastMessage
    );
  }

  get #firstDirectMessageUser() {
    return this.args.channel?.chatable?.users?.[0];
  }

  @action
  startTrackingStatus() {
    this.#firstDirectMessageUser?.statusManager.trackStatus();
  }

  @action
  stopTrackingStatus() {
    this.#firstDirectMessageUser?.statusManager.stopTrackingStatus();
  }

  <template>
    <LinkTo
      @route="chat.channel"
      @models={{@channel.routeModels}}
      class={{dConcatClass
        "chat-channel-row"
        (if @channel.focused "focused")
        (if @channel.currentUserMembership.muted "muted")
        (if @options.leaveButton "can-leave")
        (if (eq this.chat.activeChannel.id @channel.id) "active")
        (if this.channelHasUnread "has-unread")
        (if this.isLongPressing "is-long-pressed")
      }}
      tabindex="0"
      data-chat-channel-id={{@channel.id}}
      {{didInsert this.startTrackingStatus}}
      {{willDestroy this.stopTrackingStatus}}
      {{(if this.shouldRemoveChannel (modifier this.onRemoveChannel))}}
      {{ChatOnLongPress
        this.onLongPressStart
        this.onLongPressEnd
        this.onLongPressCancel
      }}
    >
      <div
        class={{dConcatClass
          "chat-channel-row__content"
          (if @channel.isCategoryChannel "is-category" "is-dm")
          (if this.shouldReset "-animate-reset")
        }}
        {{(if this.shouldHandleSwipe (modifier this.registerSwipableRow))}}
        {{(if this.shouldHandleSwipe (modifier this.handleSwipe))}}
        {{(if this.shouldReset (modifier this.onReset))}}
        style={{this.rowStyle}}
      >
        <ChannelIcon @channel={{@channel}} />
        <div class="chat-channel-row__info">
          <div class="chat-channel-row__name-container">
            <ChannelName @channel={{@channel}} @unreadIndicator={{true}} />
          </div>
          <ChatChannelMetadata @channel={{@channel}} />
          {{#if this.shouldRenderLastMessage}}
            <div class="chat-channel__last-message">
              {{dReplaceEmoji (trustHTML @channel.lastMessage.excerpt)}}
            </div>
          {{/if}}
        </div>

        {{#if
          (and @options.leaveButton @channel.isFollowing this.site.desktopView)
        }}
          <ToggleChannelMembershipButton
            @channel={{@channel}}
            @options={{hash
              leaveClass="btn-flat chat-channel-leave-btn"
              labelType="none"
              leaveIcon="xmark"
              leaveTitle=(if
                @channel.isDirectMessageChannel
                this.leaveDirectMessageLabel
                this.leaveChannelLabel
              )
            }}
          />
        {{/if}}
      </div>

      {{#if this.showRemoveButton}}
        <div
          class={{dConcatClass
            "chat-channel-row__action-btn"
            (if this.isSwipeToClearNotifications "--clear" "--remove")
            (if this.isAtThreshold "-at-threshold" "-not-at-threshold")
          }}
        >
          {{dIcon
            (if this.isSwipeToClearNotifications "circle-check" "circle-xmark")
          }}
        </div>
      {{/if}}
    </LinkTo>
  </template>
}
